import { isSome } from '@mbscan/shared'

// iOS の Chrome / Firefox は WebKit の viewport inset API を実装しておらず、下部ツールバーが
// 表示されていても viewport 単位（100dvh / 100svh）と fixed 配置の基準がツールバー込みの高さで
// 計算される（crbug.com/40944174）。そのため初回表示では画面下端に貼り付けた UI（ボトムシートの
// CTA・シャッターバー等）がツールバーの裏に隠れ、アプリを切り替えて戻る等で OS がビューを
// 再レイアウトすると直る、という症状になる。visualViewport は実際の可視高さを返すため、
// その実測値を CSS カスタムプロパティへ流し込み、globals.css 側で 100dvh との差分
// （--viewport-bottom-gap）を下端 UI の逃げ幅として上乗せする。
export const VISUAL_VIEWPORT_HEIGHT_PROPERTY = '--visual-viewport-height'

// ソフトキーボードによる visualViewport の縮小（一般に 250px 以上）と、ツールバー分の
// 見誤り（50〜100px 程度）を区別するしきい値。キーボードまで補正に含めると、入力のたびに
// ボトムシートやフッターがキーボード分せり上がる大きな挙動変化になるため反映しない
const KEYBOARD_GAP_THRESHOLD_PX = 150

export type ViewportMeasurement = {
  visualViewportHeight: number
  visualViewportScale: number
  innerHeight: number
}

// CSS へ反映すべき可視高さ（px）を返す。反映せず直前の値を保つべき局面では null を返す
export const resolveViewportHeightToApply = ({
  visualViewportHeight,
  visualViewportScale,
  innerHeight,
}: ViewportMeasurement): number | null => {
  // ピンチズーム中は visualViewport.height がズーム倍率で縮むため、レイアウトの基準に使えない
  if (Math.abs(visualViewportScale - 1) > 0.01) {
    return null
  }
  if (innerHeight - visualViewportHeight > KEYBOARD_GAP_THRESHOLD_PX) {
    return null
  }
  return Math.round(visualViewportHeight)
}

export const trackVisualViewportHeight = (): void => {
  const viewport = window.visualViewport
  if (!isSome(viewport)) {
    return
  }
  const update = () => {
    const height = resolveViewportHeightToApply({
      visualViewportHeight: viewport.height,
      visualViewportScale: viewport.scale,
      innerHeight: window.innerHeight,
    })
    if (isSome(height)) {
      document.documentElement.style.setProperty(VISUAL_VIEWPORT_HEIGHT_PROPERTY, `${height}px`)
    }
  }
  update()
  viewport.addEventListener('resize', update)
  // アプリ切替からの復帰（bfcache 含む）や画面回転で visualViewport の resize が飛ばない
  // ケースがあるため、明示的に測り直す
  window.addEventListener('pageshow', update)
  window.addEventListener('orientationchange', update)
}
